import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import type { GalleryItem } from '@/data/gallery';
import type { Lang } from '@/pages/Index';
import { t } from '@/data/translations';

interface LightboxProps {
  items: GalleryItem[];
  currentIndex: number;
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (index: number) => void;
  lang: Lang;
  captionKeys: string[];
}

const Lightbox = ({ items, currentIndex, isOpen, onClose, onNavigate, lang, captionKeys }: LightboxProps) => {
  const tr = t(lang).gallery;
  const [carouselSlide, setCarouselSlide] = useState(0);

  const goTo = useCallback((index: number) => {
    if (index < 0) onNavigate(items.length - 1);
    else if (index >= items.length) onNavigate(0);
    else onNavigate(index);
  }, [items.length, onNavigate]);

  // Reset carousel slide when navigating to a new item
  useEffect(() => {
    setCarouselSlide(0);
  }, [currentIndex]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') {
        const current = items[currentIndex];
        if (current?.type === 'carousel' && current.slides && carouselSlide > 0) {
          setCarouselSlide((s) => s - 1);
        } else {
          goTo(currentIndex - 1);
        }
      }
      if (e.key === 'ArrowRight') {
        const current = items[currentIndex];
        if (current?.type === 'carousel' && current.slides && carouselSlide < current.slides.length - 1) {
          setCarouselSlide((s) => s + 1);
        } else {
          goTo(currentIndex + 1);
        }
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, currentIndex, carouselSlide, onClose, goTo, items]);

  if (!isOpen) return null;

  const item = items[currentIndex];
  const currentSlides = item.type === 'carousel' && item.slides ? item.slides : null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-background/95 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-6 right-6 p-2 text-muted-foreground hover:text-foreground transition-colors duration-300 cursor-pointer z-10"
        aria-label="Fermer"
      >
        <X className="w-5 h-5" strokeWidth={1.5} />
      </button>

      {/* Counter */}
      <span className="absolute top-6 left-6 text-xs text-muted-foreground tracking-widest">
        {currentIndex + 1} / {items.length}
      </span>

      {/* Previous */}
      {items.length > 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); goTo(currentIndex - 1); }}
          className="absolute left-4 md:left-8 p-3 text-muted-foreground/50 hover:text-foreground transition-all duration-300 cursor-pointer group"
          aria-label="Précédent"
        >
          <ChevronLeft className="w-6 h-6 transition-transform duration-300 group-hover:-translate-x-1" strokeWidth={1} />
        </button>
      )}

      {/* Next */}
      {items.length > 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); goTo(currentIndex + 1); }}
          className="absolute right-4 md:right-8 p-3 text-muted-foreground/50 hover:text-foreground transition-all duration-300 cursor-pointer group"
          aria-label="Suivant"
        >
          <ChevronRight className="w-6 h-6 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1} />
        </button>
      )}

      {/* Content */}
      <div
        className="max-w-5xl max-h-[85vh] px-16 flex flex-col items-center gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        {item.type === 'image' && (
          <img
            src={item.src}
            alt={item.alt || ''}
            className="max-h-[75vh] w-auto object-contain rounded-sm"
          />
        )}

        {item.type === 'video' && (
          <video
            src={item.src}
            controls
            autoPlay
            loop
            playsInline
            className="max-h-[75vh] w-auto object-contain rounded-sm"
          />
        )}

        {item.type === 'carousel' && currentSlides && (
          <div className="flex flex-col items-center gap-4">
            <img
              src={currentSlides[carouselSlide]}
              alt={item.alt || ''}
              className="max-h-[70vh] w-auto object-contain rounded-sm transition-opacity duration-500"
            />
            <div className="flex items-center gap-2.5">
              {currentSlides.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCarouselSlide(i)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
                    i === carouselSlide
                      ? 'bg-foreground scale-125'
                      : 'bg-muted-foreground/40 hover:bg-muted-foreground/70'
                  }`}
                  aria-label={`Slide ${i + 1}`}
                />
              ))}
            </div>
          </div>
        )}

        {captionKeys[currentIndex] && tr.captions[captionKeys[currentIndex]] && (
          <p className="text-xs text-muted-foreground leading-relaxed text-center max-w-lg">
            {tr.captions[captionKeys[currentIndex]]}
            {item.captionLink && (
              <>
                {' '}{tr.moreInfo}{' '}
                <a
                  href={item.captionLink.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline underline-offset-2 hover:text-foreground transition-colors duration-300"
                >
                  {item.captionLink.label}
                </a>
              </>
            )}
          </p>
        )}
      </div>
    </div>
  );
};

export default Lightbox;
